import { generateImageEffects } from '@/api/imageEffects';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import { ImageEffectType } from '@/typings/customilyElements/imageEffectType';
import { uploadImageToS3 } from '@/utils/imageUtils';
import { applyFaceAdjustment } from '@/utils/engraver-set-image-face-adjustment';
import { buildCloudFrontUrl, isAbsoluteUrl } from '@/utils/pathUtils';

export class ImageEffectsService {
    private static nextFileId = 1;
    private static fileIds = new WeakMap<File, number>();
    private static pendingRequests = new Map<string, Promise<{ base64Image: string; testingUrl?: string }>>();

    private static async setImageElement(
        element: ImagePlaceholderElement,
        base64Image: string,
        canvas: CanvasEx,
        metadata?: Record<string, unknown>
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const image = new Image();

            image.onload = () => {
                try {
                    element.setElement(image);
                    applyFaceAdjustment(element, metadata);
                    canvas.requestRenderAll();
                    resolve();
                } catch (error) {
                    console.error('Error setting image element:', error);
                    reject(new Error(`Failed to set image element: ${error}`));
                }
            };

            image.onerror = (event) => {
                const errorMessage = 'Failed to load image with effects';
                console.error(errorMessage, event);
                reject(new Error(errorMessage));
            };

            image.crossOrigin = 'Anonymous';
            image.src = `data:image/webp;base64,${base64Image}`;
        });
    }
    private static parseImageEffects(raw: unknown): ImageEffectType[] {
        try {
            if (typeof raw === 'string') return JSON.parse(raw) as ImageEffectType[];
            return (raw as ImageEffectType[]) || [];
        } catch (_error) {
            return (raw as ImageEffectType[]) || [];
        }
    }

    private static buildEffectsKey(effects: ImageEffectType[]): string {
        const stripped = effects.map((effect) => {
            const copy = { ...effect } as Partial<ImageEffectType> & { id?: string; presetId?: string };
            delete copy.id;
            delete copy.presetId;
            return copy;
        });
        return JSON.stringify(stripped);
    }

    private static getTestingUrl(uploadPath: string): string {
        return isAbsoluteUrl(uploadPath) ? uploadPath : buildCloudFrontUrl(uploadPath);
    }

    private static getFileId(file: File): number {
        const existingId = this.fileIds.get(file);
        if (existingId) return existingId;

        const id = this.nextFileId++;
        this.fileIds.set(file, id);
        return id;
    }

    private static getRequestKey(data: { effectsKey: string; username: string; file: File }): string {
        const { effectsKey, username, file } = data;
        return `${username}:${this.getFileId(file)}:${effectsKey}`;
    }

    private static async generateAndPersistImage(data: {
        effects: ImageEffectType[];
        effectsKey: string;
        username: string;
        file: File;
    }): Promise<{ base64Image: string; testingUrl?: string; metadata?: Record<string, unknown> }> {
        const requestKey = this.getRequestKey(data);
        const pendingRequest = this.pendingRequests.get(requestKey);
        if (pendingRequest) return pendingRequest;

        const request = (async () => {
            const response = await generateImageEffects({
                imageEffects: data.effects,
                username: data.username,
                file: data.file
            });

            const { url: base64Image, metadata } = response;
            try {
                const dataUrl = `data:image/webp;base64,${base64Image}`;
                const uploadResponse = await uploadImageToS3('preview', dataUrl);
                return {
                    base64Image,
                    metadata,
                    testingUrl: this.getTestingUrl(uploadResponse.Path)
                };
            } catch (uploadError) {
                console.error('Failed to upload AI-generated image effect to S3:', uploadError);
                return { base64Image, metadata };
            }
        })();

        this.pendingRequests.set(requestKey, request);

        try {
            return await request;
        } finally {
            this.pendingRequests.delete(requestKey);
        }
    }

    public static async processImageEffects(data: {
        objects: ImagePlaceholderElement[];
        canvas: CanvasEx;
        username: string;
        file: File;
    }) {
        const { objects, canvas, username, file } = data;
        if (!objects?.length) return;

        const groups = new Map<string, { effects: ImageEffectType[]; objects: ImagePlaceholderElement[] }>();

        for (const object of objects) {
            const effects = this.parseImageEffects(object?.imageEffects);
            if (!effects.length) continue;

            const key = this.buildEffectsKey(effects);
            const existing = groups.get(key);
            if (existing) {
                existing.objects.push(object);
            } else {
                groups.set(key, { effects, objects: [object] });
            }
        }

        if (!groups.size) return;

        await Promise.all(
            Array.from(groups.entries()).map(async ([effectsKey, { effects, objects: groupObjects }]) => {
                const { base64Image, testingUrl, metadata } = await this.generateAndPersistImage({
                    effects,
                    effectsKey,
                    username,
                    file
                });
                await Promise.all(
                    groupObjects.map((object) => this.setImageElement(object, base64Image, canvas, metadata))
                );

                if (testingUrl) {
                    groupObjects.forEach((object) => {
                        object.testingUrl = testingUrl;
                    });
                }
            })
        );
    }
}
